/**
 * video-stabilizer.js — the stabilisation maths, with no DOM and no I/O.
 *
 *   luma frames → per-frame global motion → camera trajectory
 *              → smoothed trajectory → per-frame correction + zoom
 *
 * Split out of the tool page on purpose: every number this file produces is
 * checkable in node against a reference clip, which is how the algorithm was
 * chosen in the first place. See tests/node/video-stabilizer.test.mjs.
 *
 * ── Why this algorithm ──────────────────────────────────────────────────────
 * Замеры на фикстурах с ИЗВЕСТНОЙ истиной (неподвижная подложка, кадры
 * вырезаны из неё со сдвигом и поворотом — tests/fixtures/video-stabilizer/
 * make-fixtures.py), судья — СКО второй разности восстановленной траектории
 * (jitter.py) и прямой остаток между соседними кадрами (для поворота, который
 * проекционный судья почти не видит).
 *
 *   ТРЯСКА ПЕРЕНОСОМ            дрожь(СКО)      ТРЯСКА С ЗАВАЛОМ    остаток
 *     вход                          13.58         вход                15.90
 *     vidstab (эталон отрасли)       4.95         vidstab              1.09
 *     deshake (есть в нашем ядре)    4.80         этот модуль          ~1.0
 *     этот модуль, ±5 / 11.9% кроп   0.77         (потолок цепочки)    0.84
 *     ровный клип (пол прибора)      0.30
 *
 * То есть по переносу мы обходим эталон отрасли в 6.4 раза, а по завалу встаём
 * с ним вровень — и всё это без второго 30-мегабайтного ядра.
 *
 * ⚠️ Числа до 2026-08-05 в истории этого файла и коммитов НЕ сравнимы с этими:
 * прежний судья считал проекции с шагом 2 по обеим осям, писал их в разреженный
 * индекс и потому корреллировал две ГРЕБЁНКИ — сигнал существовал только на
 * чётных сдвигах, мнимый «пол 2.00» был шагом решётки. Он же приписывал deshake
 * порчу ровного видео (2→4): на исправленном приборе ровный клип у deshake
 * 0.299 → 0.013, то есть тот довод был ложным. Вывод «делаем свой» устоял по
 * другой причине — по числам выше.
 *
 * Motion estimation itself is NOT reimplemented here: it reuses
 * /js/remove-text/motion-estimator.js (FFT phase correlation, sub-pixel,
 * CSP-clean). Перенос берётся полнокадровой фазовой корреляцией, поворот —
 * блочной аффинной оценкой с RANSAC оттуда же; изоляционная сверка с истиной
 * дала перенос 0.115 px и угол 0.094° остатка при размахе ±7.3 px / ±1.86°.
 *
 * Loads as window.VideoStabilizer (browser) and module.exports (node tests).
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.VideoStabilizer = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* Working resolution for motion estimation. Powers of two on both axes:
       the estimator resamples to the nearest power of two internally, and a
       non-power-of-two height silently squashes the frame before correlation.
       256×128 costs ~15 ms/frame in plain JS and is plenty — the estimate is
       scaled back to source pixels by the caller-supplied sx/sy. */
    var EST_W = 256, EST_H = 128;

    /* Разрешение для оценки ПОВОРОТА — отдельное от EST_W/EST_H и обязательно
       с СОХРАНЁННЫМ соотношением сторон: в анизотропно сжатом кадре поворот
       перестаёт быть поворотом, и угол занижается (замерено: на 256×128 масштаб
       1.583, то есть теряется 58% завала; при верном соотношении 1.02).
       Замер точность/цена на фикстуре «сдвиг + завал», блок 32:
         320×240 → 86 мс/кадр, остаток 0.095°
         192×144 → 23 мс/кадр, остаток 0.110°   ← берём: вчетверо дешевле
         160×120 → 15 мс/кадр, остаток 0.242°   ← корреляция падает 0.995→0.974
       Блок 32, а не 64: при 64 на 16:9 остаётся 3 блока, а RANSAC'у нужно от
       шести, и на реальном кадре с движущимся объектом он молча свалится в
       чистый перенос. При 32 блоков 18 (16:9) и 24 (4:3) — есть что отбраковать. */
    var EST_ROT_LONG = 192, EST_ROT_BLOCK = 32;

    /* Размер кадра для оценки поворота: длинная сторона EST_ROT_LONG, короткая —
       строго по соотношению сторон.

       ⚠️ Округлять стороны до кратности блока НЕЛЬЗЯ, хотя соблазн есть (тогда
       блоки покрывают кадр без остатка). Для 16:9 это даёт 192×96 вместо
       192×108 — пропорция уезжает на 12%, а искажённая пропорция и есть та
       самая причина, по которой угол занижался на анизотропном 256×128.
       Пусть лучше крайняя полоса не попадёт в блоки: она стоит доли процента
       охвата, а пропорция стоит точности угла.

       Короткая сторона не опускается ниже двух блоков — иначе на широком кадре
       остаётся один ряд блоков, RANSAC'у нечего отбраковывать, и первый же
       движущийся объект в кадре уводит оценку. */
    function estRotSize(w, h) {
        if (!w || !h) return { w: EST_ROT_LONG, h: EST_ROT_LONG };
        var ratio = Math.min(w, h) / Math.max(w, h);
        var short = Math.max(2 * EST_ROT_BLOCK, Math.round(EST_ROT_LONG * ratio));
        var long = Math.max(EST_ROT_LONG, Math.round(short / ratio));
        return w >= h ? { w: long, h: short } : { w: short, h: long };
    }

    /* Цель по остаточной дрожи — доля КОРОТКОЙ стороны кадра, чтобы порог значил
       одно и то же на 480p и на 4K. 0.25% — это 1.2 px на кадре высотой 480.

       ⚠️ Обоснование, стоявшее здесь до 05.08, опиралось на прибор, у которого
       минимальный ненулевой отсчёт равнялся 2.0 (проекции считались гребёнкой),
       и звучало как «дальше ±5 покупаешь только обрезку — судья всё равно на
       полу». Пол был артефактом решётки, а не свойством мира. Само число
       выжило проверку исправленным прибором и потому остаётся, но НЕ по прежней
       причине: на фикстуре с известной истиной оно даёт 13.58 → 0.77 при полe
       0.30 и обрезке 11.9%, а дальнейшее сужение цели упирается в шум оценки
       (0.2–0.3 px), а не в сглаживание.

       Не перекалибровывать по одной фикстуре: метрика планировщика считается на
       СГЛАЖЕННОМ пути, который гладок по построению, и потому систематически
       оптимистичнее того, что окажется в файле. Сверять только с выходными
       файлами (tests/playwright/video-stabilizer.mjs). */
    var JITTER_TARGET_RATIO = 0.0025;
    var JITTER_TARGET = 1.2;                 // fallback when the size is unknown

    /* ВТОРОЙ порог, выше цели: ниже него клип уже ровный, и трогать его нельзя
       вовсе. Это не то же число, что цель, и быть тем же не может — цель говорит,
       насколько сильно давить, КОГДА есть что убирать, а этот порог решает,
       браться ли за файл. Без него первая сборка списывала 7.1% кадра на «ремонт»
       съёмки, которой ремонт не требовался: на таком уровне поправки лежат внутри
       собственного шума оценщика, то есть тул подмешивает дрожь вместо того,
       чтобы её снимать. Проверка исправленным прибором: ровный клип проходит
       мимо планировщика, обрезка 0%, дрожь 0.299 → 0.168. */
    var STEADY_ENOUGH_RATIO = 0.005;

    /* Порог «в клипе есть завал». Ниже него поворот не правится вовсе, а перенос
       берётся у полнокадровой оценки.

       Почему выбор вообще нужен: НИ ОДИН из двух оценщиков не лучше всегда, и
       это не случайность, а физика. Полнокадровая корреляция смазывается
       поворотом (кадр поворачивается — единого пика нет), а блочная аффинная
       подгоняет шесть параметров туда, где движение описывается двумя, и лишние
       четыре впитывают шум. Замер на фикстурах с известной истиной, суммарный
       остаток оценки переноса:

         чистый перенос      полнокадровая 0.296 px · блочная 0.887 · среднее 0.459
         перенос + завал     полнокадровая 0.806 px · блочная 0.403 · среднее 0.441

       Среднее двух — не выход: оно проигрывает лучшему в обоих случаях.
       Выбирать надо по тому, ЕСТЬ ли завал, и это измеримо: медиана |угла|
       читается 0.000° на ровном клипе и 0.061° на трясущемся без завала против
       0.93° и 0.65° на клипах с завалом. Порог 0.25° лежит в пятнадцать раз
       выше шума и в два с половиной раза ниже сигнала. */
    var ROLL_PRESENT_DEG = 0.25;

    /* Порог НЕДОВЕРИЯ к собственной оценке — доля короткой стороны кадра.
       2% это 7.2 px на кадре 360.

       Зачем: крупный движущийся объект (проехавшая машина, идущий вплотную
       человек) перетягивает оценку на себя, а едет он ПЛАВНО — то есть
       выглядит как панорама без дрожи. Тул в этом случае не портит файл, он
       делает хуже: объявляет трясущийся клип ровным и отказывается работать.
       Замер на фикстурах «объект на 10/25/45% кадра поверх известной тряски»:
       истинная дрожь как у rolling (23.5), а тул читал 0.4…1.6 и писал
       «почти ровно».

       Признак — РАСХОЖДЕНИЕ двух независимых оценщиков (они по-разному
       взвешивают объект), причём по 90-му процентилю, а не по медиане: объект
       портит не все пары, а те, где он попадает на много блоков сразу.

         ровный / трясущийся / с завалом   90-й процентиль 0.00 · 1.53 · 1.59 px
         объект на 25% кадра (тул СПРАВЛЯЕТСЯ)               5.56 … 7.24 px
         передний план во весь кадр (справиться нельзя)     15.89 … 16.25 px

       Порог 3% = 10.8 px на кадре 360: выше самого тяжёлого случая, с которым
       тул справляется, и заметно ниже того, с которым не справляется. ⚠️ Брать
       2% нельзя, хотя чистые клипы и там далеко: разброс «объектного» случая от
       прогона к прогону доходит до 7.24 (RANSAC случаен), и порог 7.2 сделал бы
       проверку мигающей — тул то работал бы, то отказывался на одном файле.
       ⚠️ Доля инлаеров RANSAC для этого НЕ ГОДИТСЯ — замерена и отвергнута: на
       клипе со сплошным передним планом она 100%, потому что блоки на этом
       плане согласны между собой не хуже фоновых. */
    var DISAGREE_LIMIT_RATIO = 0.03;

    /* Яркость кадра из RGBA холста — та же формула (BT.601), что стояла в
       блейде с первой сборки: оценщик откалиброван на ней, и воркер обязан
       получать ровно те же числа, что получал главный поток. */
    function lumaFromRGBA(rgba, n) {
        var d = rgba, out = new Float64Array(n);
        for (var i = 0, k = 0; i < n; i++, k += 4) out[i] = 0.299 * d[k] + 0.587 * d[k + 1] + 0.114 * d[k + 2];
        return out;
    }

    /* Разбор одного кадра и его пары с предыдущим — ОДНА реализация на воркер
       (analyze-worker.js) и на запасной путь в главном потоке. Две копии этих
       строк разошлись бы молча, а замеры 05.08 (перенос 0.296 px, угол
       0.110°) держатся только на этой последовательности вызовов.

       R — RtMotion (motion-estimator.js), cfg — геометрия разбора:
         estW/estH   кадр переноса (EST_W × EST_H), rotW/rotH — кадр поворота;
         sx/sy       пикселей исходника на пиксель кадра переноса,
         rsx/rsy     то же для кадра поворота; rotMotionCap — порог в пикселях
                     исходника, выше которого блоки поворота не верны.
       prev — то, что вернул прошлый вызов для ПРЕДЫДУЩЕГО кадра (или null);
       idx — номер кадра: сид случайности RANSAC (см. seeded). */
    /* Сеяный генератор (mulberry32): RANSAC пары получает случайность от НОМЕРА
       кадра, а не от Math.random — один и тот же файл даёт один и тот же ответ
       при каждом прогоне, в воркере и в главном потоке одинаково. */
    function seeded(n) {
        var a = (Math.imul(n + 1, 0x9E3779B1) ^ 0x5EED5EED) | 0;
        return function () {
            a = (a + 0x6D2B79F5) | 0;
            var t = Math.imul(a ^ (a >>> 15), 1 | a);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }

    function analyseFrame(R, rgba, rrgba, prev, cfg, idx) {
        var cur = R.prepareFrameFull(lumaFromRGBA(rgba, cfg.estW * cfg.estH), cfg.estW, cfg.estH, null, {});
        var rotOpts = { block: EST_ROT_BLOCK, step: EST_ROT_BLOCK, rand: seeded(idx || 0) };
        var curRot = R.prepareFrameBlocks(lumaFromRGBA(rrgba, cfg.rotW * cfg.rotH), cfg.rotW, cfg.rotH, null, rotOpts);
        var pair = null;
        if (prev) {
            var r = R.globalTranslation(prev.full, cur);
            var mx = r.x * cfg.sx, my = r.y * cfg.sy, th = 0, ax = null, ay = null, rt = 0, ro = 0;
            if (prev.rot && Math.hypot(mx, my) < cfg.rotMotionCap) {
                rt = 1;
                var aff = R.estimateAffineFromPrepared(prev.rot, curRot, rotOpts);
                /* Требуем не просто «получилось», а согласия шести блоков:
                   меньше — и один движущийся в кадре объект утащит оценку за
                   собой. Не вышло — остаёмся на полнокадровом переносе без
                   угла. Смещение — перемещение ЦЕНТРА кадра: матрица задана от
                   левого верхнего угла, и там в перенос подмешан поворот. */
                if (aff && aff.affine && aff.inliers >= 6) {
                    var a = aff.affine, hx = cfg.rotW / 2, hy = cfg.rotH / 2;
                    ax = (a[0] * hx + a[1] * hy + a[2] - hx) * cfg.rsx;
                    ay = (a[3] * hx + a[4] * hy + a[5] - hy) * cfg.rsy;
                    th = Math.atan2(a[3], a[0]);
                    ro = 1;
                }
            }
            pair = { x: mx, y: my, th: th, ax: ax, ay: ay, rt: rt, ro: ro };
        }
        return { state: { full: cur, rot: curRot }, pair: pair };
    }

    /* Геометрия разбора для кадра W×H (размер ПОКАЗА). */
    function analyseConfig(W, H) {
        var RS = estRotSize(W, H);
        return {
            estW: EST_W, estH: EST_H, rotW: RS.w, rotH: RS.h,
            sx: W / EST_W, sy: H / EST_H, rsx: W / RS.w, rsy: H / RS.h,
            /* Блок ищет сдвиг внутри себя — движение больше трети блока делает
               его оценку выдумкой. Порог в пикселях ИСХОДНИКА. */
            rotMotionCap: (EST_ROT_BLOCK / 3) * (W / RS.w)
        };
    }

    /**
     * Выбрать источник оценки по всему клипу разом (не покадрово: переключение
     * от кадра к кадру подмешивало бы в траекторию разницу двух оценщиков —
     * ступеньку на ровном месте).
     *
     * @param {Array<{x,y,th,ax,ay}>} pairs  x/y — полнокадровая оценка,
     *        ax/ay — блочная, th — угол от блочной (радианы).
     * @return {Array<{x,y,th}>} + свойство rollPresent
     */
    function resolvePairs(pairs, opts) {
        opts = opts || {};
        var ths = [], dis = [];
        for (var i = 0; i < pairs.length; i++) {
            var p = pairs[i];
            ths.push(Math.abs(p.th || 0));
            if (p.ax != null && p.ay != null) dis.push(Math.hypot(p.ax - p.x, p.ay - p.y));
        }
        ths.sort(function (a, b) { return a - b; });
        dis.sort(function (a, b) { return a - b; });
        var medDeg = ths.length ? ths[ths.length >> 1] * 180 / Math.PI : 0;
        var roll = medDeg >= ROLL_PRESENT_DEG;
        var out = pairs.map(function (q) {
            return roll
                ? { x: (q.ax == null ? q.x : q.ax), y: (q.ay == null ? q.y : q.ay), th: q.th || 0 }
                : { x: q.x, y: q.y, th: 0 };
        });
        out.rollPresent = roll;
        out.rollMedianDeg = Math.round(medDeg * 1000) / 1000;

        /* Насколько два оценщика согласны между собой. Считается ВСЕГДА, даже
           когда завала нет: объект перетягивает и полнокадровую оценку тоже. */
        var p90 = dis.length ? dis[Math.min(dis.length - 1, Math.floor(dis.length * 0.9))] : 0;
        var shortSide = opts.shortSide || 0;
        out.disagreeP90 = Math.round(p90 * 100) / 100;
        /* Медиана того же расхождения — рабочая оценка СОБСТВЕННОГО шума
           оценки в пикселях. Ниже неё улучшать нечего, и планировщик по ней
           понимает, когда лишняя обрезка перестала что-либо покупать. */
        out.noiseFloor = dis.length ? Math.round(dis[dis.length >> 1] * 100) / 100 : 0;
        out.trustworthy = !shortSide || !dis.length || p90 <= shortSide * DISAGREE_LIMIT_RATIO;
        return out;
    }

    /**
     * Убрать уровни, которые стоят дороже, а лучше не делают.
     *
     * Уровни отличаются потолком обрезки и строгостью цели, поэтому «сильный»
     * уровень почти всегда находит окно шире и обрезает больше. Проблема в том,
     * что метрика планировщика считается на СГЛАЖЕННОМ пути и потому всегда
     * обещает выигрыш, а в файле его нет: остаток упирается в шум оценки, а
     * более широкое окно даёт БОЛЬШИЕ поправки и вносит этого шума больше.
     *
     * Замер на клипе с завалом (три независимых прогона, один результат):
     *   «рекомендуемая» обрезка 13.4% → дрожь 1.342, завал 0.054°
     *   «максимум»      обрезка 26.2% → дрожь 1.515, завал 0.092°
     * то есть вдвое большая обрезка за 13% УХУДШЕНИЯ, при обещании 0.4 против
     * 0.5 на вывеске. Предлагать такую сделку — обманывать выбором.
     *
     * Правило: сильный уровень остаётся, только если слабому ЕЩЁ ЕСТЬ куда
     * улучшаться — то есть его прогноз выше собственного шума оценки. Сравнивать
     * уровни между собой недостаточно: на замере выше прогноз обещал 0.6 → 0.4,
     * «улучшение на треть», и любой относительный порог такое пропустит. Но
     * треть от величины, лежащей ВНУТРИ шума, — не улучшение, а разница между
     * двумя случайными числами.
     *
     * Шум берётся не из воздуха: это расхождение двух независимых оценщиков
     * (медиана), которое и так считается в resolvePairs — на исправных клипах
     * 0.50…0.74 px. Прогноз «рекомендуемой» на клипе с завалом равен 0.6, то
     * есть уже на этом уровне; значит платить обрезкой дальше не за что.
     *
     * Снятый уровень получает план слабого, и карточки честно показывают
     * одинаковые числа — это и означает «дальше платить нечем».
     */
    function dropPointlessLevels(plans, order, opts) {
        opts = opts || {};
        var gain = opts.minGain == null ? 0.25 : opts.minGain;
        var floor = opts.noiseFloor || 0;
        for (var i = 1; i < order.length; i++) {
            var weak = plans[order[i - 1]], strong = plans[order[i]];
            if (!weak || !strong) continue;
            var costs = strong.cropPct > weak.cropPct + 0.05;
            var atFloor = floor > 0 && weak.jitterAfter <= floor;
            var better = strong.jitterAfter <= weak.jitterAfter * (1 - gain);
            if (costs && (atFloor || !better)) plans[order[i]] = weak;
        }
        return plans;
    }

    /* ⛔ Медианная отбраковка «сорвавшихся» оценок ПРОВЕРЕНА И ОТВЕРГНУТА
       (05.08). Идея выглядит очевидной: траектория — накопленная сумма, одна
       промахнувшаяся пара сдвигает весь дальнейший путь, и максимум остатка у
       нас был 11.6 против 2.75 у эталона. Но фильтр по окрестности (медиана +
       MAD, окно ±3, порог 4σ) сделал ХУЖЕ: остаток 2.29 → 2.66, максимум
       11.6 → 23.5. Причина в частоте: у тряски с рук период около четырёх
       кадров, и окно ±3 накрывает почти два периода — «выброс» относительно
       соседей там и есть настоящее движение. Абсолютный порог не спасает по
       той же причине: настоящий рывок камеры неотличим от брака ПО ВЕЛИЧИНЕ.
       Если возвращаться к этой задаче — отбраковывать надо по УВЕРЕННОСТИ
       оценщика (inliers у аффинной, response у полнокадровой), а не по
       значению; это единственный признак, не зависящий от спектра движения. */

    /**
     * Integrate per-pair motion into a camera trajectory.
     * @param {Array<{x:number,y:number,th?:number}>} pairs  motion of the CONTENT
     *        between consecutive frames, already in SOURCE pixels (th in radians).
     * @return {Array<{x:number,y:number,th:number}>} cumulative path, length = pairs+1
     */
    function trajectory(pairs) {
        var out = [{ x: 0, y: 0, th: 0 }];
        for (var i = 0; i < pairs.length; i++) {
            out.push({
                x: out[i].x + pairs[i].x,
                y: out[i].y + pairs[i].y,
                th: out[i].th + (pairs[i].th || 0)
            });
        }
        return out;
    }

    /**
     * Moving average over ±win frames (the shape vidstab's `smoothing` uses),
     * with the ENDS extrapolated linearly rather than the window truncated.
     *
     * Truncating is the obvious implementation and it is wrong in a way that
     * shows: a steady PAN is a straight trajectory, whose moving average is
     * itself — except near the ends, where a shortened window bends it. The
     * first build did that and charged 1.8% crop for stabilising a clip that
     * had no shake at all (caught by the pan case in the node test). Padding
     * with the edge SLOPE keeps a straight path exactly straight, so a smooth
     * pan costs nothing, and it also stops the drift that otherwise appears in
     * the first and last second of every clip.
     */
    function smooth(traj, win) {
        var n = traj.length, out = new Array(n);
        if (n === 0) return out;
        var th = function (p) { return p.th || 0; };
        var d0 = n > 1 ? { x: traj[1].x - traj[0].x, y: traj[1].y - traj[0].y, th: th(traj[1]) - th(traj[0]) }
                       : { x: 0, y: 0, th: 0 };
        var d1 = n > 1 ? { x: traj[n - 1].x - traj[n - 2].x, y: traj[n - 1].y - traj[n - 2].y,
                           th: th(traj[n - 1]) - th(traj[n - 2]) }
                       : { x: 0, y: 0, th: 0 };
        var at = function (i) {
            if (i < 0) return { x: traj[0].x + d0.x * i, y: traj[0].y + d0.y * i, th: th(traj[0]) + d0.th * i };
            if (i > n - 1) return { x: traj[n - 1].x + d1.x * (i - n + 1), y: traj[n - 1].y + d1.y * (i - n + 1),
                                    th: th(traj[n - 1]) + d1.th * (i - n + 1) };
            return traj[i];
        };
        for (var i = 0; i < n; i++) {
            var ax = 0, ay = 0, aa = 0, c = 0;
            for (var k = i - win; k <= i + win; k++) { var p = at(k); ax += p.x; ay += p.y; aa += th(p); c++; }
            out[i] = { x: ax / c, y: ay / c, th: aa / c };
        }
        return out;
    }

    /**
     * Residual jitter of a path — the same judge used to pick this algorithm:
     * median |second difference|. Second difference, not first, because a
     * steady PAN has a large first difference and no jitter at all; punishing
     * it would fight the operator instead of the shake.
     */
    function jitterOf(path, radiusPx) {
        if (path.length < 3) return 0;
        /* Поворот переводится в ПИКСЕЛИ смещения на радиусе кадра: точка на
           расстоянии r от центра при повороте θ уезжает на r·θ. Без этого
           планировщик подбирал бы окно по одному переносу и объявлял победу на
           клипе, который продолжает заваливаться — измерено: на фикстуре
           «сдвиг + завал» тул снимал перенос полностью, а остаток оставался
           равен чистому повороту. Радиус — половина диагонали: угловая дрожь
           заметнее всего по углам кадра, и глаз ловит именно их. */
        var r = radiusPx || 0;
        var v = [];
        for (var i = 1; i < path.length - 1; i++) {
            var d2x = path[i + 1].x - 2 * path[i].x + path[i - 1].x;
            var d2y = path[i + 1].y - 2 * path[i].y + path[i - 1].y;
            var d2t = r ? ((path[i + 1].th || 0) - 2 * (path[i].th || 0) + (path[i - 1].th || 0)) * r : 0;
            v.push(Math.abs(d2x) + Math.abs(d2y) + Math.abs(d2t));
        }
        v.sort(function (a, b) { return a - b; });
        return v[v.length >> 1];
    }

    /* Пофреймовая «сила рывка» пути — то же, что медианит jitterOf, но по
       кадрам: из неё рисуется график тряски на таймлайне. Длина = path.length;
       крайние кадры берут значение соседа (второй разности у них нет). */
    function profile(path, radiusPx) {
        var n = path.length, out = new Float32Array(n), r = radiusPx || 0;
        for (var i = 1; i < n - 1; i++) {
            var d2x = path[i + 1].x - 2 * path[i].x + path[i - 1].x;
            var d2y = path[i + 1].y - 2 * path[i].y + path[i - 1].y;
            var d2t = r ? ((path[i + 1].th || 0) - 2 * (path[i].th || 0) + (path[i - 1].th || 0)) * r : 0;
            out[i] = Math.abs(d2x) + Math.abs(d2y) + Math.abs(d2t);
        }
        if (n > 2) { out[0] = out[1]; out[n - 1] = out[n - 2]; }
        return out;
    }

    /**
     * Turn a trajectory into what the renderer needs.
     *
     * @param {Array} traj  camera path in source pixels
     * @param {Object} opts
     *   width, height   — source frame size (for the crop percentage)
     *   maxCropPct      — hard ceiling on how much frame the user agrees to
     *                     lose (0..60). The search never exceeds it.
     *   target          — jitter to reach (default 2.0)
     *   window          — force a specific window instead of searching
     *   untrusted       — оценке движения верить нельзя (см. resolvePairs):
     *                     план становится пустым, файл не трогаем
     *   mode            — 'smooth' (по умолчанию: движение оператора остаётся,
     *                     дрожь уходит) | 'lock' («штатив»: камера стоит)
     *   roll            — 'follow' (по умолчанию: завал сглаживается, как
     *                     перенос) | 'lock' (завал держится постоянным)
     *   tiltDeg         — постоянный наклон всего ролика, градусы: выровнять
     *                     заваленный горизонт. Потолком обрезки НЕ урезается —
     *                     это прямая просьба человека, а не цена стабилизации.
     * Без mode/roll/tiltDeg поведение — прежнее, байт в байт: на нём стоят
     * замеры 05.08 и tests/playwright/video-stabilizer.mjs.
     * @return {{corrections:Array<{x,y,th}>, path:Array<{x,y,th}>, zoom:number,
     *           cropPct:number, window:number, jitterBefore:number, jitterAfter:number}}
     */
    function plan(traj, opts) {
        opts = opts || {};
        var W = opts.width || 0, H = opts.height || 0;
        var maxCropPct = Math.max(0, Math.min(60, opts.maxCropPct == null ? 12 : opts.maxCropPct));
        /* Абсолютная цель в пикселях: доля короткой стороны кадра, чтобы
           один и тот же порог значил одно и то же на 480p и на 4K. Уровень
           («минимум обрезки» / «максимум стабильности») задаёт МНОЖИТЕЛЬ к
           ней, а не своё число в чужих единицах. */
        var minDimT = Math.min(W || 0, H || 0);
        var baseTarget = minDimT > 0 ? minDimT * JITTER_TARGET_RATIO : JITTER_TARGET;
        var target = opts.target == null ? baseTarget : (opts.targetIsRatio ? minDimT * opts.target : opts.target);
        var radius = (W && H) ? Math.hypot(W, H) / 2 : 0;
        var before = jitterOf(traj, radius);
        var minDimT0 = Math.min(W || 0, H || 0);
        var mode = opts.mode === 'lock' ? 'lock' : 'smooth';
        var rollLock = opts.roll === 'lock';
        var tiltDeg = +opts.tiltDeg || 0;
        var tilt = tiltDeg * Math.PI / 180;

        /* Already steady → do NOTHING. Not "stabilise a little": a clip that is
           already at tripod level has nothing to gain and everything to lose,
           because any correction costs crop. Measured on a genuinely steady
           fixture, the first build still charged 7.1% of the frame to "fix"
           jitter 2.3 — which is exactly the sin `deshake` was rejected for
           (it doubled the jitter of steady footage). The tool that knows when
           not to act is the one worth shipping. */
        /* Оценке нельзя верить — не трогаем файл ВООБЩЕ.
           Предупредить и всё равно выдать результат было бы хуже, чем молчать:
           замерено на съёмке «через сетку», где передний план накрывает кадр
           целиком и оценка цепляется за него — тул выдавал файл ВДВОЕ хуже
           исходного (дрожь 6.4 → 15.0) при уверенном «−98%» на экране. Это тот
           самый грех, за который был отвергнут deshake; под своим именем он
           хуже вдвойне. Предупреждение остаётся, но теперь оно объясняет, ПОЧЕМУ
           тул ничего не сделал, а не украшает испорченный файл. */
        /* «Штатив» и «горизонт» — прямые просьбы человека, и ровный клип их не
           отменяет: у него бывает медленный дрейф, которого второй разностью
           не видно. Наклон не зависит от оценки движения вовсе и остаётся даже
           там, где оценке не верим. */
        var steadyEnough = minDimT0 > 0 ? minDimT0 * STEADY_ENOUGH_RATIO : (JITTER_TARGET * 2);
        if (opts.untrusted || (mode === 'smooth' && !rollLock && before <= steadyEnough && !opts.window)) {
            var still = traj.map(function () { return { x: 0, y: 0, th: tilt }; });
            var zStill = tilt ? zoomFor(still) : 1;
            return {
                corrections: still,
                path: traj.map(function (p) { return { x: p.x, y: p.y, th: p.th || 0 }; }),
                zoom: zStill, cropPct: Math.round((1 - 1 / zStill) * 1000) / 10,
                cropNeeded: Math.round((1 - 1 / zStill) * 1000) / 10, scale: 1,
                maxShiftPx: 0, maxRollDeg: 0, window: 0,
                alreadySteady: !opts.untrusted, untrusted: !!opts.untrusted,
                jitterBefore: Math.round(before * 100) / 100,
                jitterAfter: Math.round(before * 100) / 100,
                mode: mode, rollLock: rollLock, tiltDeg: tiltDeg
            };
        }

        /* Наименьший зум, при котором ВСЕ четыре угла выходного кадра ещё
           берутся из исходника. С одним переносом хватало формулы «1 + 2·сдвиг
           / короткая сторона», но повёрнутый кадр съедает края неравномерно, и
           та же формула молча выпускала чёрные клинья в углах. Здесь честная
           обратная развёртка ТОГО ЖЕ отображения, что рисует drawStabilised:
               out = c + z·(R(θ)·(src − c) + d)   ⇒
               src = c + R(−θ)·(out − c)/z − R(−θ)·d
           и для каждого угла берётся z, при котором точка ещё внутри кадра.
           Порядок «сначала поворот, потом сдвиг» — не вкусовщина: именно он
           доказан офлайн-замером (остаток 1.08 при потолке цепочки 0.84), и
           расхождение с обратным порядком составляет |d|·θ, то есть доли
           пикселя — но обе половины обязаны быть одной формулой, иначе зум
           считается не для той картинки, которую рисуют. */
        function zoomFor(corr) {
            var need = 1;
            var hx = (W || 1) / 2, hy = (H || 1) / 2;
            var cs = [[-hx, -hy], [hx, -hy], [hx, hy], [-hx, hy]];
            for (var i = 0; i < corr.length; i++) {
                var c = corr[i], th = c.th || 0, co = Math.cos(-th), si = Math.sin(-th);
                var wx = co * c.x - si * c.y, wy = si * c.x + co * c.y;   // R(−θ)·d
                for (var k = 0; k < 4; k++) {
                    var ux = cs[k][0], uy = cs[k][1];
                    var vx = co * ux - si * uy, vy = si * ux + co * uy;
                    var limX = vx > 0 ? (hx + wx) : (hx - wx);
                    var limY = vy > 0 ? (hy + wy) : (hy - wy);
                    if (limX > 1e-6) need = Math.max(need, Math.abs(vx) / limX);
                    if (limY > 1e-6) need = Math.max(need, Math.abs(vy) / limY);
                }
            }
            return need;
        }

        /* Постоянная точка «штатива» — СЕРЕДИНА РАЗМАХА по каждой оси, а не
           среднее: до неё все кадры дотягиваются наименьшим сдвигом, то есть
           наименьшей обрезкой. Среднее тянулось бы туда, где камера провела
           больше времени, и платило бы обрезкой за каждый редкий дальний
           выход. Тот же приём у «горизонта» — только для угла. */
        var LOCK = null;
        if (mode === 'lock' || rollLock) {
            var mnx = Infinity, mxx = -Infinity, mny = Infinity, mxy = -Infinity, mnt = Infinity, mxt = -Infinity;
            for (var li = 0; li < traj.length; li++) {
                var q = traj[li], qt = q.th || 0;
                if (q.x < mnx) mnx = q.x;
                if (q.x > mxx) mxx = q.x;
                if (q.y < mny) mny = q.y;
                if (q.y > mxy) mxy = q.y;
                if (qt < mnt) mnt = qt;
                if (qt > mxt) mxt = qt;
            }
            LOCK = traj.length ? { x: (mnx + mxx) / 2, y: (mny + mxy) / 2, th: (mnt + mxt) / 2 } : { x: 0, y: 0, th: 0 };
        }

        function targetPath(win) {
            if (mode === 'lock') return traj.map(function () { return { x: LOCK.x, y: LOCK.y, th: LOCK.th }; });
            var sm = smooth(traj, win);
            if (rollLock) for (var i = 0; i < sm.length; i++) sm[i] = { x: sm[i].x, y: sm[i].y, th: LOCK.th };
            return sm;
        }

        function evaluate(win) {
            var sm = targetPath(win);
            var corr = new Array(traj.length), maxShift = 0, maxRoll = 0;
            for (var i = 0; i < traj.length; i++) {
                var c = { x: sm[i].x - traj[i].x, y: sm[i].y - traj[i].y,
                          th: (sm[i].th || 0) - (traj[i].th || 0) + tilt };
                corr[i] = c;
                var m = Math.hypot(c.x, c.y);
                if (m > maxShift) maxShift = m;
                if (Math.abs(c.th - tilt) > maxRoll) maxRoll = Math.abs(c.th - tilt);
            }
            var zoom = zoomFor(corr);
            return { corr: corr, sm: sm, maxShift: maxShift, maxRoll: maxRoll, zoom: zoom,
                     cropPct: (1 - 1 / zoom) * 100, jitter: jitterOf(sm, radius) };
        }

        /* «Штатив» окна не ищет: цель одна — стоять, и цена её известна сразу.
           Не влезла в потолок обрезки — finish ужмёт поправки, и камера будет
           «плавать» в пределах разрешённого; сколько план просил на самом
           деле, уходит наружу как cropNeeded, чтобы сказать об этом честно. */
        if (mode === 'lock') return finish(evaluate(0), 0);
        if (opts.window) {
            var forced = evaluate(opts.window);
            return finish(forced, opts.window);
        }
        /* Smallest window that reaches the target — measured: ±5 already hits
           jitter 2.0 at 11.6% crop where ±20 costs 40.3% for no gain. Walking
           up from the cheapest end is therefore not an optimisation, it is the
           difference between keeping and losing a third of the picture. */
        var best = null, bestWin = 0;
        for (var win = 3; win <= 60; win += (win < 12 ? 1 : 4)) {
            var r = evaluate(win);
            if (!best) { best = r; bestWin = win; }
            if (r.cropPct > maxCropPct) break;          // past what the user allows
            best = r; bestWin = win;
            if (r.jitter <= target) break;              // good enough — stop paying crop
        }
        return finish(best, bestWin);

        function finish(r, win) {
            /* Respect the crop ceiling even when the search had to stop early:
               scale the corrections down rather than hand back a zoom the user
               refused. Partial stabilisation beats an unexpected 40% crop.

               С поворотом связь «во сколько ужать поправки» ↔ «какой выйдет
               зум» перестала быть формулой: угол съедает края нелинейно.
               Поэтому — двоичный поиск по множителю, а зум каждый раз считается
               той же честной проверкой углов. Двадцати шагов хватает на 1e-6. */
            var out = r, k = 1;
            var zoomCap = 1 / (1 - maxCropPct / 100);
            if (r.zoom > zoomCap && (r.maxShift > 0 || r.maxRoll > 0)) {
                var lo = 0, hi = 1;
                for (var it = 0; it < 20; it++) {
                    var mid = (lo + hi) / 2;
                    if (zoomFor(scaleCorr(r.corr, mid)) <= zoomCap) lo = mid; else hi = mid;
                }
                k = lo;
                var ck = scaleCorr(r.corr, k);
                var smK = r.sm.map(function (s, i) {
                    return { x: traj[i].x + k * (s.x - traj[i].x),
                             y: traj[i].y + k * (s.y - traj[i].y),
                             th: (traj[i].th || 0) + k * ((s.th || 0) - (traj[i].th || 0)) };
                });
                out = { corr: ck, sm: smK, maxShift: r.maxShift * k, maxRoll: r.maxRoll * k,
                        zoom: zoomFor(ck), cropPct: (1 - 1 / zoomFor(ck)) * 100,
                        jitter: jitterOf(smK, radius) };
            }
            return {
                corrections: out.corr,
                path: out.sm,
                zoom: out.zoom,
                cropPct: Math.round(out.cropPct * 10) / 10,
                cropNeeded: Math.round(r.cropPct * 10) / 10,
                scale: Math.round(k * 1000) / 1000,
                maxShiftPx: Math.round(out.maxShift * 10) / 10,
                maxRollDeg: Math.round((out.maxRoll || 0) * 180 / Math.PI * 100) / 100,
                window: win,
                jitterBefore: Math.round(before * 100) / 100,
                jitterAfter: Math.round(out.jitter * 100) / 100,
                mode: mode, rollLock: rollLock, tiltDeg: tiltDeg
            };
        }

        /* Наклон — просьба человека, а не цена стабилизации: потолок обрезки
           ужимает только поправку движения, наклон остаётся целиком. */
        function scaleCorr(corr, k) {
            return corr.map(function (c) { return { x: c.x * k, y: c.y * k, th: ((c.th || 0) - tilt) * k + tilt }; });
        }
    }

    /**
     * Draw one stabilised frame. Kept here (not in the page) so the browser and
     * the node reference test apply the SAME geometry — a warp that differs
     * between the test and the tool proves nothing.
     */
    function drawStabilised(ctx, source, W, H, correction, zoom) {
        var dx = correction ? correction.x : 0;
        var dy = correction ? correction.y : 0;
        var th = correction ? (correction.th || 0) : 0;

        if (!th) {
            /* Без поворота — прежний путь через ИСХОДНЫЙ ПРЯМОУГОЛЬНИК, ровно
               тот, которым измерены 13.58 → 0.77. Обратное отображение
               «масштаб от центра, затем сдвиг»:
                   src(out) = (out − W/2)/zoom + W/2 − dx
               то есть окно шириной W/zoom начинается в (W − W/zoom)/2 − dx. */
            var sw = W / zoom, sh = H / zoom;
            var sx = (W - sw) / 2 - dx;
            var sy = (H - sh) / 2 - dy;
            if (typeof source === 'function') {
                // WebCodecsPipeline geometry2d: video(sx,sy,sw,sh, dx,dy,dw,dh)
                source(sx, sy, sw, sh, 0, 0, W, H);
                return;
            }
            ctx.save();
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(source, sx, sy, sw, sh, 0, 0, W, H);
            ctx.restore();
            return;
        }

        /* С поворотом исходный прямоугольник бессилен — повёрнутую область
           прямоугольником не задать. Собираем то же отображение матрицей:
               out = c + zoom·(R(θ)·(src − c) + d)
           что при θ = 0 в точности сводится к ветке выше (там d вычитается из
           начала окна, здесь прибавляется к содержимому — одно и то же).

           ⚠️ Здесь стояло, что так нельзя: «конвейер отдаёт geometry2d ФУНКЦИЮ
           отрисовки, а функцию в трансформацию не подашь». Это оказалось
           неверно — функция внутри зовёт drawImage на том же контексте и
           потому подчиняется его матрице (в ветке с поворотом контейнера она
           к тому же оборачивает себя в save/restore, так что наша матрица
           уцелеет). Проверено сквозным замером: «сдвиг + завал» 15.90 → 1.0,
           вровень с vidstab, тогда как без поворота оставалось 5.39. */
        ctx.save();
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(W / 2, H / 2);
        ctx.scale(zoom, zoom);
        ctx.translate(dx, dy);
        ctx.rotate(th);
        ctx.translate(-W / 2, -H / 2);
        if (typeof source === 'function') source(0, 0, W, H, 0, 0, W, H);
        else ctx.drawImage(source, 0, 0, W, H, 0, 0, W, H);
        ctx.restore();
    }


    /**
     * Отрисовщик кадра — ОДИН на предпросмотр и на файл. Картинка, которую
     * человек видит до экспорта, собирается тем же кодом, что и файл: иначе
     * превью доказывает только само себя.
     *
     *   var r = createRenderer();
     *   r.draw(ctx, source, srcW, srcH, plan, i, { fill: 'crop' | 'synth' });
     *   r.plain(ctx, source, srcW, srcH, zoom);   // исходный кадр, тот же зум
     *   r.reset();                                 // перемотка: соседей больше нет
     *
     * ctx — холст вывода ЛЮБОГО размера: математика идёт в пикселях исходника
     * (srcW × srcH — размер показа), к холсту её приводит масштаб. Так один
     * код обслуживает и экспорт 720p из 1080p, и превью в 640 px.
     * source — то, что умеет drawImage (<video>, VideoFrame, ImageBitmap), или
     * функция конвейера drawVideo(sx,sy,sw,sh,dx,dy,dw,dh); она рисует ТОЛЬКО
     * в собственный холст конвейера, поэтому всё из источника идёт через ctx.
     *
     * fill 'synth' — «весь кадр». Зума нет; полосы, которые открыл сдвиг,
     * берутся из ПРОШЛОГО ВЫХОДНОГО кадра, сдвинутого вместе со сценой: путь
     * вывода гладкий, и неподвижный фон ложится ровно на своё место. Где
     * прошлого кадра нет (начало ролика, перемотка) — размытая копия текущего.
     * Premiere называет это Synthesize Edges; онлайн так не делает никто.
     * Порядок слоёв: размытие → прошлый кадр → текущий кадр → запомнить.
     */
    function createRenderer() {
        var acc = null, accCtx = null, lastI = -1;
        var tiny = null, tinyCtx = null;
        function canvas(w, h) {
            if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
            var c = document.createElement('canvas'); c.width = w; c.height = h; return c;
        }
        function drawSource(ctx, source, sx, sy, sw, sh, dx, dy, dw, dh) {
            if (typeof source === 'function') source(sx, sy, sw, sh, dx, dy, dw, dh);
            else ctx.drawImage(source, sx, sy, sw, sh, dx, dy, dw, dh);
        }
        /* Кадр по поправке — то же отображение, что drawStabilised (и тот же
           порядок «поворот, потом сдвиг», на котором стоит zoomFor), только в
           пикселях исходника под масштабом холста. */
        function drawFrame(ctx, source, W, H, c, zoom) {
            var dx = c ? c.x : 0, dy = c ? c.y : 0, th = c ? (c.th || 0) : 0;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            if (!th) {
                var sw = W / zoom, sh = H / zoom;
                drawSource(ctx, source, (W - sw) / 2 - dx, (H - sh) / 2 - dy, sw, sh, 0, 0, W, H);
                return;
            }
            ctx.save();
            ctx.translate(W / 2, H / 2);
            ctx.scale(zoom, zoom);
            ctx.translate(dx, dy);
            ctx.rotate(th);
            ctx.translate(-W / 2, -H / 2);
            drawSource(ctx, source, 0, 0, W, H, 0, 0, W, H);
            ctx.restore();
        }
        /* Размытие без ctx.filter: кадр → крошечная копия → растянуть обратно
           со сглаживанием. Работает везде (у Safari ctx.filter годами не было)
           и стоит два крошечных рисования вместо гауссова прохода по кадру. */
        function blurCover(ctx, source, srcW, srcH, outW, outH) {
            var tw = Math.max(8, Math.round(outW / 28)), th = Math.max(8, Math.round(outH / 28));
            if (!tiny || tiny.width !== tw || tiny.height !== th) {
                tiny = canvas(tw, th);
                tinyCtx = tiny.getContext('2d', { alpha: false });
            }
            tinyCtx.imageSmoothingEnabled = true;
            tinyCtx.imageSmoothingQuality = 'high';
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            if (typeof source === 'function') {
                // Источник-функция рисует только в свой холст: уменьшаем в угол
                // ctx и забираем оттуда — следующий слой всё равно накроет угол.
                drawSource(ctx, source, 0, 0, srcW, srcH, 0, 0, tw, th);
                tinyCtx.drawImage(ctx.canvas, 0, 0, tw, th, 0, 0, tw, th);
            } else {
                tinyCtx.drawImage(source, 0, 0, srcW, srcH, 0, 0, tw, th);
            }
            // Чуть шире кадра: размытая копия не должна показывать своих краёв.
            var bx = outW * 0.05, by = outH * 0.05;
            ctx.drawImage(tiny, 0, 0, tw, th, -bx, -by, outW + 2 * bx, outH + 2 * by);
            ctx.restore();
        }
        return {
            reset: function () { lastI = -1; },
            /* Исходный кадр без поправки, с тем же зумом, что у результата:
               сравнение «до/после» обязано отличаться ТОЛЬКО тряской. */
            plain: function (ctx, source, srcW, srcH, zoom) {
                var cv = ctx.canvas;
                ctx.save();
                ctx.setTransform(cv.width / srcW, 0, 0, cv.height / srcH, 0, 0);
                drawFrame(ctx, source, srcW, srcH, null, zoom || 1);
                ctx.restore();
            },
            draw: function (ctx, source, srcW, srcH, plan, i, opts) {
                opts = opts || {};
                var cv = ctx.canvas, outW = cv.width, outH = cv.height;
                var kx = outW / srcW, ky = outH / srcH;
                var cs = plan && plan.corrections;
                var corr = cs && cs.length ? cs[Math.max(0, Math.min(i, cs.length - 1))] : null;
                var synth = opts.fill === 'synth';
                var zoom = synth ? 1 : ((plan && plan.zoom) || 1);
                if (synth) {
                    if (!acc || acc.width !== outW || acc.height !== outH) {
                        acc = canvas(outW, outH);
                        accCtx = acc.getContext('2d', { alpha: false });
                        lastI = -1;
                    }
                    blurCover(ctx, source, srcW, srcH, outW, outH);
                    /* Прошлый кадр — сосед, только пока идём вперёд маленькими
                       шагами: после перемотки или пропуска он показал бы не
                       ту сцену. Предпросмотр пропускает кадры, файл — никогда. */
                    var P = plan && plan.path;
                    if (P && lastI >= 0 && i > lastI && i - lastI <= 4 && P[i] && P[lastI]) {
                        var ddx = (P[i].x - P[lastI].x) * kx, ddy = (P[i].y - P[lastI].y) * ky;
                        var dth = (P[i].th || 0) - (P[lastI].th || 0);
                        ctx.save();
                        ctx.setTransform(1, 0, 0, 1, 0, 0);
                        ctx.translate(outW / 2 + ddx, outH / 2 + ddy);
                        ctx.rotate(dth);
                        ctx.translate(-outW / 2, -outH / 2);
                        ctx.drawImage(acc, 0, 0);
                        ctx.restore();
                    }
                }
                ctx.save();
                ctx.setTransform(kx, 0, 0, ky, 0, 0);
                drawFrame(ctx, source, srcW, srcH, corr, zoom);
                ctx.restore();
                if (synth) {
                    accCtx.drawImage(cv, 0, 0);
                    lastI = i;
                }
            }
        };
    }

    /* Где в ИСХОДНОМ кадре лежит то, что попадёт в результат: четыре угла
       выходного кадра, развёрнутые обратно тем же отображением
           src = c + R(−θ)·((out − c)/z − d).
       Рамка «плавает» по трясущемуся кадру — ровно то, что делает
       стабилизация, видно глазом. */
    function cropQuad(plan, i, W, H, zoom) {
        var cs = plan && plan.corrections;
        var c = cs && cs.length ? cs[Math.max(0, Math.min(i, cs.length - 1))] : { x: 0, y: 0, th: 0 };
        var z = zoom || (plan && plan.zoom) || 1, th = c.th || 0;
        var co = Math.cos(-th), si = Math.sin(-th), hx = W / 2, hy = H / 2;
        return [[-hx, -hy], [hx, -hy], [hx, hy], [-hx, hy]].map(function (p) {
            var ox = p[0] / z - c.x, oy = p[1] / z - c.y;
            return { x: hx + co * ox - si * oy, y: hy + si * ox + co * oy };
        });
    }

    return {
        EST_W: EST_W, EST_H: EST_H, JITTER_TARGET: JITTER_TARGET, JITTER_TARGET_RATIO: JITTER_TARGET_RATIO,
        STEADY_ENOUGH_RATIO: STEADY_ENOUGH_RATIO,
        EST_ROT_LONG: EST_ROT_LONG, EST_ROT_BLOCK: EST_ROT_BLOCK, estRotSize: estRotSize,
        ROLL_PRESENT_DEG: ROLL_PRESENT_DEG, DISAGREE_LIMIT_RATIO: DISAGREE_LIMIT_RATIO,
        resolvePairs: resolvePairs, dropPointlessLevels: dropPointlessLevels,
        trajectory: trajectory,
        smooth: smooth,
        jitterOf: jitterOf,
        plan: plan,
        profile: profile,
        drawStabilised: drawStabilised,
        createRenderer: createRenderer,
        cropQuad: cropQuad,
        lumaFromRGBA: lumaFromRGBA,
        analyseFrame: analyseFrame,
        analyseConfig: analyseConfig
    };
});
